import * as React from 'react'
import { Slider as SliderPrimitive } from '@base-ui/react/slider'

import { cn } from '@/registry/reapit/lib/utils'

// ---------------------------------------------------------------------------
// Slider — migrated from Radix to Base UI
// ---------------------------------------------------------------------------
// Structural changes:
//   Radix:   Root > Track > Range + Thumb(s)
//   Base UI: Root > Control > Track > Indicator + Thumb(s)
//
// Part renames:
//   Radix Range     → Base UI Indicator
//   (new)           → Base UI Control (clickable interactive area wrapper)
//
// Value type:
//   Radix:   value/defaultValue are always number[]
//   Base UI: number | readonly number[]
//   We accept both array and single-number forms.
//
// onValueChange:
//   Radix:   (value: number[]) => void
//   Base UI: (value, eventDetails) => void
//   We wrap to preserve the Radix-compatible single-arg signature.
//
// Data attributes:
//   data-disabled remains the same
//   data-orientation remains the same
// ---------------------------------------------------------------------------

export namespace Slider {
  export interface Props extends Omit<
    React.ComponentProps<typeof SliderPrimitive.Root>,
    'onValueChange' | 'onValueCommitted' | 'value' | 'defaultValue' | 'render'
  > {
    /** Controlled value. Pass an array for range sliders. */
    value?: number[]
    /** Uncontrolled initial value. Pass an array for range sliders. */
    defaultValue?: number[]
    /** Callback fired when the slider value changes. */
    onValueChange?: (value: number[]) => void
    /** Callback fired when the user finishes dragging. */
    onValueCommitted?: (value: number[]) => void
  }
}

function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  onValueChange,
  onValueCommitted,
  ...props
}: Slider.Props) {
  const _values = React.useMemo(
    () => (Array.isArray(value) ? value : Array.isArray(defaultValue) ? defaultValue : [min, max]),
    [value, defaultValue, min, max],
  )

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      onValueChange={
        onValueChange
          ? (val) => {
              const arr = Array.isArray(val) ? (val as number[]) : [val as number]
              onValueChange(arr)
            }
          : undefined
      }
      onValueCommitted={
        onValueCommitted
          ? (val) => {
              const arr = Array.isArray(val) ? (val as number[]) : [val as number]
              onValueCommitted(arr)
            }
          : undefined
      }
      className={cn(
        'relative flex w-full touch-none items-center select-none data-[disabled]:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col',
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Control data-slot="slider-control" className="flex w-full items-center">
        {/* DS Slider: track uses bg-muted (neutral-050) unfilled, bg-primary for filled range */}
        <SliderPrimitive.Track
          data-slot="slider-track"
          className={cn(
            'relative grow overflow-hidden rounded-full bg-muted data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5',
          )}
        >
          <SliderPrimitive.Indicator
            data-slot="slider-range"
            className={cn(
              'absolute bg-primary data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full',
            )}
          />
        </SliderPrimitive.Track>
        {Array.from({ length: _values.length }, (_, index) => (
          // DS: thumb — size-4 (16px), border-primary, bg-background (white), focus ring-ring/50 (purple-300)
          <SliderPrimitive.Thumb
            data-slot="slider-thumb"
            key={index}
            index={index}
            className="block size-4 shrink-0 rounded-full border border-primary bg-background shadow-sm ring-ring/50 transition-[color,box-shadow] hover:ring-4 focus-visible:ring-4 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50"
          />
        ))}
      </SliderPrimitive.Control>
    </SliderPrimitive.Root>
  )
}

export { Slider }
